import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shared/components/ui/select';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContractTemplates, useRates } from '../../../CreateEdit/hooks';
import { useChangeRate } from '../../hooks/useChangeRate';
import { useToast } from '@shared/hooks/useToast';

interface ChangeRateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: number;
}

type ChangeType = 'upgrade' | 'downgrade' | 'correction';

export const ChangeRateModal = ({ open, onOpenChange, teamId }: ChangeRateModalProps) => {
  const { t } = useTranslation('viewClientModule');
  const { toast } = useToast();

  const { rates, loading: ratesLoading } = useRates();
  const { templates, loading: templatesLoading } = useContractTemplates();
  const { changeRateAsync, isPending, reset } = useChangeRate(teamId);

  const [formData, setFormData] = useState({
    rateId: '',
    changeType: '' as ChangeType | '',
    requiresContract: false,
    docusingTemplateId: '',
    reason: '',
  });

  const rateOptions = rates.map((rate: any) => ({
    label: rate.title || rate.name || `Tarifa ${rate.id}`,
    value: rate.id.toString(),
  }));

  const templateOptions = templates.map((template) => ({
    label: template.name,
    value: template.id,
  }));

  const handleClose = () => {
    setFormData({
      rateId: '',
      changeType: '',
      requiresContract: false,
      docusingTemplateId: '',
      reason: '',
    });
    reset();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!formData.rateId || !formData.changeType) {
      return;
    }

    if (formData.requiresContract && !formData.docusingTemplateId) {
      return;
    }

    try {
      await changeRateAsync({
        rate_id: parseInt(formData.rateId),
        change_type: formData.changeType as ChangeType,
        requires_contract: formData.requiresContract,
        docusing_template_id: formData.requiresContract ? formData.docusingTemplateId : undefined,
        reason: formData.reason || undefined,
      });

      toast({
        title: t('changeRate.success.title'),
        description: t('changeRate.success.description'),
      });

      handleClose();
    } catch (error) {
      toast({
        title: t('changeRate.error.title'),
        description: t('changeRate.error.description'),
        variant: 'destructive',
      });
    }
  };

  const isFormValid =
    formData.rateId && formData.changeType && (!formData.requiresContract || formData.docusingTemplateId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('changeRate.modal.title')}</DialogTitle>
          <DialogDescription>{t('changeRate.modal.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Tarifa */}
          <div className="grid gap-2">
            <Label>
              {t('changeRate.fields.rate')} <span className="text-destructive">*</span>
            </Label>
            <Combobox
              options={rateOptions}
              placeholder={t('changeRate.placeholders.rate')}
              value={formData.rateId}
              setValue={(value) => setFormData((prev) => ({ ...prev, rateId: value }))}
              className="w-full"
              disabled={ratesLoading}
            />
          </div>

          {/* Tipo de cambio */}
          <div className="grid gap-2">
            <Label>
              {t('changeRate.fields.changeType')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.changeType}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, changeType: value as ChangeType }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('changeRate.placeholders.changeType')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="upgrade">{t('changeRate.changeTypes.upgrade')}</SelectItem>
                <SelectItem value="downgrade">{t('changeRate.changeTypes.downgrade')}</SelectItem>
                <SelectItem value="correction">{t('changeRate.changeTypes.correction')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Requiere contrato */}
          <div className="flex items-center gap-3">
            <Switch
              id="requires-contract"
              checked={formData.requiresContract}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({
                  ...prev,
                  requiresContract: checked,
                  docusingTemplateId: checked ? prev.docusingTemplateId : '',
                }))
              }
            />
            <Label htmlFor="requires-contract" className="cursor-pointer">
              {t('changeRate.fields.requiresContract')}
            </Label>
          </div>

          {/* Template de DocuSign (condicional) */}
          {formData.requiresContract && (
            <div className="grid gap-2">
              <Label>
                {t('changeRate.fields.docusingTemplate')} <span className="text-destructive">*</span>
              </Label>
              <Combobox
                options={templateOptions}
                placeholder={t('changeRate.placeholders.docusingTemplate')}
                value={formData.docusingTemplateId}
                setValue={(value) => setFormData((prev) => ({ ...prev, docusingTemplateId: value }))}
                className="w-full"
                disabled={templatesLoading}
              />
            </div>
          )}

          {/* Razón (opcional) */}
          <div className="grid gap-2">
            <Label>{t('changeRate.fields.reason')}</Label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder={t('changeRate.placeholders.reason')}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isPending}>
            {t('changeRate.buttons.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!isFormValid || isPending}>
            {isPending ? t('changeRate.buttons.saving') : t('changeRate.buttons.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
